// ---------------------------------------------------------------------------
// DimensionPickerModal -- Step 1: Choose Filter Dimension
// ---------------------------------------------------------------------------
// Modal for selecting which dimension to filter by (Platform, Gender, etc.)
// Grouped by type: Demographics (Audience) vs Content
// Optionally shows per-dimension counts (toggle, default OFF for speed).
// ---------------------------------------------------------------------------

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FileText, Loader2, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { AUDIENCE_DIMENSIONS, CONTENT_DIMENSIONS } from '../constants';
import type { DimensionConfig } from '../constants';
import type { DimensionCountsData } from '@/services/v11/funnelService';
import type { DataSourceId } from '@/components/v11/discovery';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DimensionPickerModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSelectDimension: (dimension: DimensionConfig) => void;
  readonly usedDimensions?: readonly string[];
  readonly onRequestCounts?: () => Promise<DimensionCountsData | undefined>;
  readonly datasource?: DataSourceId; // Filter dimensions by datasource applicability
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return count.toLocaleString()
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DimensionPickerModal({
  open,
  onClose,
  onSelectDimension,
  usedDimensions = [],
  onRequestCounts,
  datasource,
}: DimensionPickerModalProps) {
  const [showCounts, setShowCounts] = useState(false);
  const [dimensionCounts, setDimensionCounts] = useState<DimensionCountsData | undefined>(undefined);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  // Filter dimensions by datasource applicability
  const applicableAudienceDimensions = useMemo(() => {
    if (!datasource) return AUDIENCE_DIMENSIONS;
    return AUDIENCE_DIMENSIONS.filter(
      (dim) => !dim.applicableTo || dim.applicableTo.includes(datasource)
    );
  }, [datasource]);

  const applicableContentDimensions = useMemo(() => {
    if (!datasource) return CONTENT_DIMENSIONS;
    return CONTENT_DIMENSIONS.filter(
      (dim) => !dim.applicableTo || dim.applicableTo.includes(datasource)
    );
  }, [datasource]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setShowCounts(false);
      setDimensionCounts(undefined);
      setIsLoadingCounts(false);
    }
  }, [open]);

  // Fetch counts when toggle is turned on
  const handleToggleCounts = useCallback(async (enabled: boolean) => {
    setShowCounts(enabled);

    if (enabled && !dimensionCounts && onRequestCounts) {
      setIsLoadingCounts(true);
      try {
        const data = await onRequestCounts();
        setDimensionCounts(data);
      } catch {
        setDimensionCounts(undefined);
      } finally {
        setIsLoadingCounts(false);
      }
    }
  }, [dimensionCounts, onRequestCounts]);

  const handleSelect = (dimension: DimensionConfig) => {
    if (!dimension.comingSoon) {
      onSelectDimension(dimension);
    }
  };

  const getCount = (type: 'audience' | 'content', key: string): number | undefined => {
    if (!showCounts || !dimensionCounts) return undefined;
    const counts = type === 'audience' ? dimensionCounts.audience : dimensionCounts.content;
    return counts[key];
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold text-v11-text-primary flex items-center gap-2">
              Select Filter Dimension
              {isLoadingCounts && (
                <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Choose a filter dimension to add</DialogDescription>

            {onRequestCounts && (
              <div className="flex items-center gap-2 mr-6 hidden">
                <span className="text-xs text-v11-text-muted">Counts</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showCounts}
                  aria-label="Toggle dimension counts"
                  onClick={() => handleToggleCounts(!showCounts)}
                  className={cn(
                    'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                    showCounts ? 'bg-purple-600' : 'bg-zinc-300 dark:bg-zinc-700'
                  )}
                >
                  <span
                    className={cn(
                      'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-md ring-0 transition-transform duration-300',
                      showCounts ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Demographics Section */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-v11-text-muted mb-4 font-semibold">
              <Users className="h-4 w-4" />
              Demographics (Audience)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {applicableAudienceDimensions.map((dimension) => {
                const isUsed = usedDimensions.includes(dimension.key);
                const isDisabled = !!dimension.comingSoon;
                const count = getCount('audience', dimension.key);
                return (
                  <button
                    key={dimension.key}
                    onClick={() => handleSelect(dimension)}
                    disabled={isDisabled}
                    className={cn(
                      'text-left p-3 rounded-lg border transition-all',
                      'bg-v11-surface border-v11-border',
                      isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : isUsed
                          ? 'border-purple-500/30 bg-purple-500/5 hover:border-purple-500/50 hover:bg-purple-500/10 cursor-pointer ring-1 ring-purple-500/20'
                          : 'hover:border-purple-500/40 hover:bg-purple-500/5 cursor-pointer'
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-v11-text-primary flex items-center gap-1.5">
                        {dimension.label}
                        {dimension.comingSoon && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide bg-purple-500/15 text-purple-400 px-1.5 py-0.5 rounded-full">
                            Soon
                          </span>
                        )}
                        {isUsed && !dimension.comingSoon && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide bg-purple-500/15 text-purple-400 px-1.5 py-0.5 rounded-full">
                            Active
                          </span>
                        )}
                      </span>
                      {showCounts && isLoadingCounts && !isDisabled && (
                        <Loader2 className="h-3 w-3 animate-spin text-purple-400/50" />
                      )}
                      {count !== undefined && !isDisabled && (
                        <span className="text-xs font-medium text-purple-400">
                          {formatCount(count)}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-v11-text-muted">
                      {dimension.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content Section */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-v11-text-muted mb-4 font-semibold">
              <FileText className="h-4 w-4" />
              Content
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {applicableContentDimensions.map((dimension) => {
                const isUsed = usedDimensions.includes(dimension.key);
                const isDisabled = !!dimension.comingSoon;
                const count = getCount('content', dimension.key);
                return (
                  <button
                    key={dimension.key}
                    onClick={() => handleSelect(dimension)}
                    disabled={isDisabled}
                    className={cn(
                      'text-left p-3 rounded-lg border transition-all',
                      'bg-v11-surface border-v11-border',
                      isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : isUsed
                          ? 'border-cyan-500/30 bg-cyan-500/5 hover:border-cyan-500/50 hover:bg-cyan-500/10 cursor-pointer ring-1 ring-cyan-500/20'
                          : 'hover:border-cyan-500/40 hover:bg-cyan-500/5 cursor-pointer'
                    )}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-v11-text-primary flex items-center gap-1.5">
                        {dimension.label}
                        {dimension.comingSoon && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide bg-cyan-500/15 text-cyan-400 px-1.5 py-0.5 rounded-full">
                            Soon
                          </span>
                        )}
                        {isUsed && !dimension.comingSoon && (
                          <span className="text-[9px] font-semibold uppercase tracking-wide bg-cyan-500/15 text-cyan-400 px-1.5 py-0.5 rounded-full">
                            Active
                          </span>
                        )}
                      </span>
                      {showCounts && isLoadingCounts && !isDisabled && (
                        <Loader2 className="h-3 w-3 animate-spin text-cyan-400/50" />
                      )}
                      {count !== undefined && !isDisabled && (
                        <span className="text-xs font-medium text-cyan-400">
                          {formatCount(count)}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-v11-text-muted">
                      {dimension.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
